import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { fetchWithcookieData, selectWithcookieData } from '../../../redux/WithcookieSlice';
import { MainBannerProps, BannerItem } from './MainBanner.types';
import { adaptMainBannerProps } from './MainBanner.adapter';
import './MainBanner.css';
import { useTranslation } from '../../../redux/TranslationHelper';

/**
 * 배너 이미지 소스 URL을 가져오는 함수
 */
const getImageSource = (banner: BannerItem, index: number): string => {
  if (!banner.icon) {
    return 'https://via.placeholder.com/1200x400';
  }
  
  // S3 URL 패턴 확인
  if (typeof banner.icon === 'string' && banner.icon.includes('amazonaws.com')) {
    return banner.icon;
  }
  
  // 상대 경로인 경우 기본 URL 추가
  if (typeof banner.icon === 'string' && banner.icon.startsWith('/')) {
    return `${window.location.origin}${banner.icon}`;
  }
  
  return banner.icon as string;
};

/**
 * 레거시 위치 속성을 새로운 위치 값으로 변환하는 함수
 */
const getPositionFromLegacy = (horizontal = 'center', vertical = 'center'): string => {
  const hPos = horizontal.toLowerCase();
  const vPos = vertical.toLowerCase();
  
  if (vPos === 'top') {
    if (hPos === 'left') return '1';
    if (hPos === 'center') return '2';
    if (hPos === 'right') return '3';
  }
  else if (vPos === 'center' || vPos === 'middle') {
    if (hPos === 'left') return '4';
    if (hPos === 'center') return '5';
    if (hPos === 'right') return '6';
  }
  else if (vPos === 'bottom') {
    if (hPos === 'left') return '7';
    if (hPos === 'center') return '8';
    if (hPos === 'right') return '9';
  }
  
  return '5'; // 기본값: 가운데 중앙
};

/**
 * 배너 데이터에 속성 설정 및 처리
 */
const processInitialBanner = (banner: BannerItem, props: any): BannerItem => {
  // 기존 배너 데이터 자체는 유지하고 누락된 속성만 기본값으로 설정
  return {
    ...banner,
    // 각 배너는 자체 속성을 가지며, 속성이 없는 경우에만 기본값 사용
    showTitle: banner.showTitle !== undefined ? banner.showTitle : true,
    showButton: banner.showButton !== undefined ? banner.showButton : true,
    
    // 기존 위치 속성을 사용하여 position 속성 설정
    position: banner.position || '5', // 기본값: 5 (가운데 중앙)
    
    // 하위 호환성을 위해 기존 horizontalPosition, verticalPosition 속성을 기반으로 position 결정
    ...((!banner.position && (banner.horizontalPosition || banner.verticalPosition)) && {
      position: getPositionFromLegacy(banner.horizontalPosition, banner.verticalPosition)
    }),
    
    textColor: banner.textColor || 'white',
    textShadow: banner.textShadow || '0 1px 2px rgba(0, 0, 0, 0.6)',
    buttonBgColor: banner.buttonBgColor || '#ff6b6b',
    buttonTextColor: banner.buttonTextColor || 'white',
    buttonHoverColor: banner.buttonHoverColor || '#ff5252',
    buttonText: banner.buttonText || '자세히 보기',
    // 버튼 속성
    transparentButton: banner.transparentButton !== undefined ? banner.transparentButton : false,
    buttonWidth: banner.buttonWidth || '300px',
    buttonHeight: banner.buttonHeight || 'auto',
    mobileButtonWidth: banner.mobileButtonWidth || 'auto',
    mobileButtonHeight: banner.mobileButtonHeight || 'auto',
    // 기존 속성 계속
    mobileFullWidth: banner.mobileFullWidth || false,
    url: banner.url || '#',
    text: banner.text || '',
    description: banner.description || '',
    videoUrl: banner.videoUrl || '',
    mediaType: banner.mediaType || (banner.videoUrl ? 'video' : 'image'),
    autoplay: banner.autoplay !== undefined ? banner.autoplay : true,
    muted: banner.muted !== undefined ? banner.muted : true,
    loop: banner.loop !== undefined ? banner.loop : true,
    hasBackground: banner.hasBackground !== undefined ? banner.hasBackground : false
  };
};

/**
 * 스타일 문자열을 객체로 변환하는 함수
 */
const parseStyleString = (styleStr: string | undefined): React.CSSProperties => {
  if (!styleStr) return {};
  
  const styleObj: Record<string, string> = {};
  const styles = styleStr.split(';');
  
  styles.forEach(style => {
    const [property, value] = style.split(':').map(item => item?.trim());
    if (property && value) {
      // 카멜 케이스로 변환 (예: background-color -> backgroundColor)
      const camelProperty = property.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
      styleObj[camelProperty] = value;
    }
  });
  
  return styleObj;
};

/**
 * MainBanner 컴포넌트
 */
const MainBannerComponent: React.FC<any> = (props) => {
  // 어댑터를 통해 props 변환
  const adaptedProps = useMemo(() => adaptMainBannerProps(props), [props]);
  
  const {
    mode = 'slider',
    style = {},
    componentProps = {},
    className = '',
    content = {},
    data = {},
    id = '',
    viewMode = 'pc',
    ...rest
  } = adaptedProps;

  const dispatch = useDispatch() as any;
  const withcookieData = useSelector(selectWithcookieData);
  const [banners, setBanners] = useState<BannerItem[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const initialDataLoaded = useRef(false);
  const isMobile = viewMode === 'mobile' || viewMode === 'tablet' || /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  
  // 다국어 처리를 위한 훅 사용
  const { t } = useTranslation();

  useEffect(() => {
    // withcookieData가 없는 경우 데이터를 가져옵니다
    if (!withcookieData) {
      dispatch(fetchWithcookieData())
        .catch((error: any) => {
          console.error(t('배너 데이터 로딩 실패:'), error);
          setIsLoading(false);
        });
    } else {
      setIsLoading(false);
    }
  }, [dispatch, withcookieData, t]);

  // componentProps에서 배너 데이터 읽기
  useEffect(() => {
    try {
      // 다양한 데이터 구조 처리를 위한 배너 데이터 찾기
      let bannerData = null;
      
      // 0. deviceProperty 객체 내의 배너 확인 (최우선)
      if (componentProps?.deviceProperty) {
        const deviceType = viewMode === 'mobile' || viewMode === 'tablet' ? 'mobile' : 'pc';
        if (componentProps.deviceProperty[deviceType]?.banners && 
            Array.isArray(componentProps.deviceProperty[deviceType].banners)) {
          bannerData = JSON.parse(JSON.stringify(componentProps.deviceProperty[deviceType].banners));
          
          // 직접 전달된 데이터 처리
          const processedData = bannerData.map((banner: BannerItem) => processInitialBanner(banner, componentProps));
          
          // 항상 새로운 배너 데이터로 업데이트
          setBanners(processedData);
          initialDataLoaded.current = true;
          setIsLoading(false);
          return;
        }
      }
      
      // 1. 직접 전달된 banners 배열
      if (componentProps?.banners && Array.isArray(componentProps.banners)) {
        bannerData = JSON.parse(JSON.stringify(componentProps.banners)); // 깊은 복사
        
        // 직접 전달된 데이터 처리
        const processedData = bannerData.map((banner: BannerItem) => processInitialBanner(banner, componentProps));
        
        // 항상 새로운 배너 데이터로 업데이트
        setBanners(processedData);
        initialDataLoaded.current = true;
        setIsLoading(false);
        return;
      } 
      // 2. data 객체 내의 banners 배열
      else if (componentProps?.data?.banners && Array.isArray(componentProps.data.banners)) {
        bannerData = JSON.parse(JSON.stringify(componentProps.data.banners)); // 깊은 복사
      }
      // 3. content 객체 내의 banners 배열
      else if (componentProps?.content?.banners && Array.isArray(componentProps.content.banners)) {
        bannerData = JSON.parse(JSON.stringify(componentProps.content.banners)); // 깊은 복사
      }
      // 4. adaptedProps.content의 banners 배열
      else if (content?.banners && Array.isArray(content.banners)) {
        bannerData = JSON.parse(JSON.stringify(content.banners)); // 깊은 복사
      }
      // 5. adaptedProps.banners 배열
      else if (adaptedProps.banners && Array.isArray(adaptedProps.banners)) {
        bannerData = JSON.parse(JSON.stringify(adaptedProps.banners)); // 깊은 복사
      }
      
      // 배너 데이터가 발견된 경우 적용
      if (bannerData && bannerData.length > 0) {
        // 데이터 처리
        const processedData = bannerData.map((banner: BannerItem) => processInitialBanner(banner, componentProps));
        
        setBanners(processedData);
        initialDataLoaded.current = true;
        setIsLoading(false);
        return;
      }
      
      // 하드코딩된 배너 데이터 제거하고 빈 배열 설정
      setBanners([]);
      initialDataLoaded.current = true;
      setIsLoading(false);
      
    } catch (error) {
      console.error(t('[배너] 데이터 처리 중 오류:'), error);
      // 오류 발생 시 빈 배열 설정
      setBanners([]);
      initialDataLoaded.current = true;
      setIsLoading(false);
    }
  }, [componentProps, viewMode, t, content, adaptedProps.banners]);
  
  // props 변경 감지 및 적용
  useEffect(() => {
    if (!componentProps) return;
    
    // banners 상태를 의존성으로 사용하지 않고 함수형 업데이트 사용
    let needsUpdate = false;
    let applyUpdate = (currentBanners: BannerItem[]): BannerItem[] => {
      let updatedBanners = [...currentBanners];
      
      // 배너 추가 감지
      if (componentProps.addBanner) {
        // 새 배너 추가 시 기본값으로 설정하되, 제공된 속성은 유지
        const newBanner = {
          // 기본값 설정
          showTitle: true,
          showButton: true,
          position: '5',
          textColor: 'white',
          textShadow: '0 1px 2px rgba(0, 0, 0, 0.6)',
          buttonBgColor: '#ff6b6b',
          buttonTextColor: 'white',
          buttonHoverColor: '#ff5252',
          buttonText: t('자세히 보기'),
          // 새로 추가된 버튼 속성
          transparentButton: false,
          buttonWidth: '300px',
          buttonHeight: 'auto',
          mobileButtonWidth: 'auto',
          mobileButtonHeight: 'auto',
          // 기존 속성 계속
          mobileFullWidth: false,
          url: '#',
          text: '',
          description: '',
          // 제공된 속성으로 덮어쓰기
          ...componentProps.addBanner
        };
        
        updatedBanners = [...updatedBanners, newBanner];
        needsUpdate = true;
      }
      
      // 배너 속성 업데이트 감지
      if (componentProps.updateBanner && componentProps.updateIndex !== undefined) {
        const updateIndex = componentProps.updateIndex;
        
        if (updateIndex >= 0 && updateIndex < updatedBanners.length) {
          const targetBanner = {...updatedBanners[updateIndex]};
          
          // 업데이트할 속성만 적용
          Object.keys(componentProps.updateBanner).forEach(key => {
            (targetBanner as any)[key] = (componentProps.updateBanner as any)[key];
          });
          
          updatedBanners[updateIndex] = targetBanner;
          needsUpdate = true;
        }
      }
      
      // 레거시: 업데이트된 스타일 또는 설정이 있는 경우, 이를 현재 배너에 적용
      if (componentProps.updateStyle && !componentProps.addBanner) {
        
        const updateIndex = componentProps.updateIndex !== undefined ? componentProps.updateIndex : 0;
        
        if (updateIndex >= 0 && updateIndex < updatedBanners.length) {
          const targetBanner = {...updatedBanners[updateIndex]};
          
          // 중요: 개별 속성 업데이트 (깊은 병합)
          Object.keys(componentProps.updateStyle).forEach(key => {
            (targetBanner as any)[key] = (componentProps.updateStyle as any)[key];
          });
          
          updatedBanners[updateIndex] = targetBanner;
          needsUpdate = true;
        }
      }
      
      // 직접 전체 배너 데이터가 업데이트된 경우
      if (componentProps.updatedBanners && Array.isArray(componentProps.updatedBanners)) {
        // 전체 배너를 변경할 때는 각 배너 데이터를 그대로 유지
        updatedBanners = componentProps.updatedBanners;
        needsUpdate = true;
      }
      
      return needsUpdate ? updatedBanners : currentBanners;
    };
    
    // 함수형 업데이트로 상태 변경
    setBanners(applyUpdate);
    
  }, [componentProps, t]); // banners 의존성 제거, t만 유지

  // 다음 배너로 이동
  const nextBanner = useCallback(() => {
    setCurrentIndex((prevIndex) => 
      prevIndex === banners.length - 1 ? 0 : prevIndex + 1
    );
  }, [banners.length]);

  // 이전 배너로 이동
  const prevBanner = useCallback(() => {
    setCurrentIndex((prevIndex) => 
      prevIndex === 0 ? banners.length - 1 : prevIndex - 1
    );
  }, [banners.length]);

  // 특정 배너로 이동
  const goToBanner = useCallback((index: number) => {
    setCurrentIndex(index);
  }, []);

  // 자동 슬라이드 효과 - 비디오가 아닌 경우에만 타이머로 작동
  useEffect(() => {
    if (banners.length <= 1 || mode === 'list') return;
    
    // 현재 배너가 비디오인지 확인
    const currentBanner = banners[currentIndex];
    const isCurrentBannerVideo = currentBanner?.mediaType === 'video' && currentBanner?.videoUrl;
    
    // 비디오가 아닌 경우에만 타이머 설정
    let interval: NodeJS.Timeout;
    if (!isCurrentBannerVideo) {
      interval = setInterval(() => {
        nextBanner();
      }, 5000);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [banners.length, mode, nextBanner, currentIndex, banners]);

  // 비디오 끝났을 때 다음 배너로 이동하는 핸들러
  const handleVideoEnded = useCallback(() => {
    setIsVideoPlaying(false);
    // 비디오가 끝나면 다음 배너로 이동
    nextBanner();
  }, [nextBanner]);

  // 비디오 클릭 이벤트 핸들러 추가
  const handleVideoClick = useCallback(() => {
    if (!videoRef.current) return;
    
    if (videoRef.current.paused) {
      videoRef.current.play().then(() => {
        setIsVideoPlaying(true);
      }).catch(error => {
        console.error(t('비디오 재생 중 오류 발생:'), error);
      });
    } else {
      videoRef.current.pause();
      setIsVideoPlaying(false);
    }
  }, [t]);

  // 마우스 이벤트 핸들러
  const handleMouseEnter = useCallback(() => {
    setIsHovering(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsHovering(false);
  }, []);

  // 비디오 재생 시작 핸들러
  const handleVideoPlaying = useCallback(() => {
    setIsVideoPlaying(true);
  }, []);

  // 비디오 로드/에러 핸들러
  const handleVideoLoaded = useCallback(() => {
    // 필요한 경우 추가 로직
  }, []);

  const handleVideoError = useCallback((e: React.SyntheticEvent<HTMLVideoElement>) => {
    console.error(t('비디오 로드 오류:'), (e.target as HTMLVideoElement).error);
  }, [t]);

  // 비디오 자동재생 강제 적용
  useEffect(() => {
    if (banners.length === 0) return;
    
    // 현재 배너가 비디오인지 확인
    const currentBanner = banners[currentIndex];
    const isVideo = currentBanner?.mediaType === 'video' && currentBanner?.videoUrl;
    
    // 비디오가 있고 videoRef가 존재하면 재생 시도
    if (isVideo && videoRef.current) {
      // 비디오 요소 초기화 (배너 전환 시 처음부터 재생되도록)
      if (videoRef.current) {
        videoRef.current.currentTime = 0;
        videoRef.current.muted = currentBanner.muted === undefined ? true : currentBanner.muted;
      }
      
      // 자동재생이 설정된 경우에만 재생 시도
      if (currentBanner.autoplay) {
        // 자동재생을 위한 함수
        const playVideo = async () => {
          try {
            // 자동재생 보장을 위한 설정
            if (videoRef.current) {
              // autoplay가 true인 경우에만 재생 시도
              const playPromise = videoRef.current.play();
              
              if (playPromise !== undefined) {
                playPromise.then(() => {
                  // 재생 성공
                }).catch(error => {
                  // 자동재생 실패 시 조용히 무시
                  // 경우에 따라 사용자 상호작용 없이 자동재생이 차단될 수 있음
                });
              }
            }
          } catch (error) {
            console.error(t('비디오 재생 중 오류 발생:'), error);
          }
        };
        
        // 비디오 재생 시도
        playVideo();
      }
    }
  }, [currentIndex, banners, t]);

  // 로딩 중 표시
  if (isLoading) {
    return <div className="mainbanner-loading">{t('배너 로딩 중...')}</div>;
  }

  // 배너가 없는 경우 빈 배너 표시
  if (!banners || banners.length === 0) {
    return (
      <div className="mainbanner-container" style={style}>
        <div className="mainbanner-slide-content" style={{ backgroundColor: '#f5f5f5' }} />
        <div className="mainbanner-content position-5" style={{ color: '#333333', textShadow: 'none' }}>
          <h2 className="mainbanner-title">{t('배너가 없습니다')}</h2>
          <p className="mainbanner-description">{t('배너를 추가해주세요')}</p>
        </div>
      </div>
    );
  }

  // 배너가 하나뿐인 경우 단일 배너 표시
  if (banners.length === 1) {
    const banner = banners[0];
    const customStyle = typeof banner.style === 'string' ? parseStyleString(banner.style) : (banner.style || {});
    const isVideo = banner.mediaType === 'video' && banner.videoUrl;
    
    // 이미지 소스 가져오기
    const imgSrc = getImageSource(banner, 0);
    
    return (
      <div 
        className={`mainbanner-container ${isMobile ? 'mobile-full-height' : ''} ${className}`} 
        style={{ ...style, ...customStyle }}
      >
        {isVideo ? (
          <div 
            className="mainbanner-video-container" 
            onClick={handleVideoClick} 
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            style={{ width: '100%', height: '100%', cursor: 'pointer', position: 'relative' }}
          >
            <video
              ref={videoRef}
              className="mainbanner-video"
              src={banner.videoUrl}
              autoPlay={banner.autoplay}
              playsInline
              muted={banner.muted}
              loop={banner.loop}
              preload="auto"
              disablePictureInPicture
              onError={handleVideoError}
              onLoadedData={handleVideoLoaded}
              onPlay={handleVideoPlaying}
              onEnded={() => !banner.loop && handleVideoEnded()}
            />
            <div 
              className="mainbanner-video-play-button" 
              onClick={(e) => {
                e.stopPropagation();
                handleVideoClick();
              }}
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                color: 'white',
                borderRadius: '50%',
                width: '60px',
                height: '60px',
                display: isHovering || !isVideoPlaying ? 'flex' : 'none',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                opacity: '0.8',
                transition: 'opacity 0.2s, display 0.2s',
                zIndex: 10
              }}
              onMouseOver={(e) => {
                (e.currentTarget as HTMLDivElement).style.opacity = '1';
              }}
              onMouseOut={(e) => {
                (e.currentTarget as HTMLDivElement).style.opacity = '0.8';
              }}
            >
              {isVideoPlaying ? (
                // 정지 아이콘
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="6" y="4" width="4" height="16" fill="white" />
                  <rect x="14" y="4" width="4" height="16" fill="white" />
                </svg>
              ) : (
                // 재생 아이콘
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M8 5V19L19 12L8 5Z" fill="white" />
                </svg>
              )}
            </div>
          </div>
        ) : (
          isMobile ? (
            <div 
              className="mainbanner-slide-content" 
              style={{ backgroundImage: `url(${imgSrc})` }}
            />
          ) : (
            <img 
              className="mainbanner-image" 
              src={imgSrc} 
              alt={banner.text || t('배너 이미지')} 
            />
          )
        )}
        
        {(banner.showTitle || banner.showButton) && (
          <div 
            className={`mainbanner-content position-${banner.position} ${banner.mobileFullWidth ? 'mobile-full-width' : ''} ${banner.hasBackground ? 'has-background' : ''}`}
            style={{ 
              color: banner.textColor || 'white',
              textShadow: banner.textShadow || '0 1px 2px rgba(0, 0, 0, 0.6)'
            }}
          >
            {banner.showTitle && banner.description && (
              <p className="mainbanner-description">{t(banner.description)}</p>
            )}
            {banner.showTitle && banner.text && (
              <h2 className="mainbanner-title">{t(banner.text)}</h2>
            )}
            
            {banner.showButton && banner.url && banner.url !== '#' && (
              <button 
                className={`mainbanner-cta-button ${banner.transparentButton ? 'transparent' : ''}`}
                onClick={() => window.location.href = banner.url || '#'}
                style={{
                  backgroundColor: banner.transparentButton ? 'transparent' : (banner.buttonBgColor || '#ff6b6b'),
                  color: banner.buttonTextColor || 'white',
                  borderColor: banner.buttonTextColor || 'white',
                  width: isMobile ? (banner.mobileButtonWidth || banner.buttonWidth || 'auto') : (banner.buttonWidth || 'auto'),
                  height: isMobile ? (banner.mobileButtonHeight || banner.buttonHeight || 'auto') : (banner.buttonHeight || 'auto')
                }}
              >
                {t(banner.buttonText || '자세히 보기')}
              </button>
            )}
          </div>
        )}
      </div>
    );
  }

  // 리스트 모드 (가로 스크롤)
  if (mode === 'list') {
    return (
      <div className={`mainbanner-list-container ${className}`} style={style}>
        {banners.map((banner, index) => {
          const customStyle = typeof banner.style === 'string' ? parseStyleString(banner.style) : (banner.style || {});
          const imgSrc = getImageSource(banner, index);
          const isVideo = banner.mediaType === 'video' && banner.videoUrl;
          
          return (
            <div key={index} className="mainbanner-list-item" style={customStyle}>
              {isVideo ? (
                <div 
                  className="mainbanner-video-container" 
                  data-index={index}
                  onClick={() => {
                    const video = document.querySelector(`.mainbanner-list-item:nth-child(${index + 1}) video`) as HTMLVideoElement;
                    if (video) {
                      if (video.paused) {
                        video.play().catch(error => console.error(t('비디오 재생 중 오류 발생:'), error));
                      } else {
                        video.pause();
                      }
                    }
                  }} 
                  onMouseEnter={(e) => {
                    const container = e.currentTarget;
                    const button = container.querySelector('.mainbanner-video-play-button') as HTMLElement;
                    if (button) button.style.display = 'flex';
                  }}
                  onMouseLeave={(e) => {
                    const container = e.currentTarget;
                    const button = container.querySelector('.mainbanner-video-play-button') as HTMLElement;
                    const video = container.querySelector('video') as HTMLVideoElement;
                    if (button && video && !video.paused) button.style.display = 'none';
                  }}
                  style={{ width: '100%', height: '100%', cursor: 'pointer', position: 'relative' }}
                >
                  <video
                    className="mainbanner-video"
                    src={banner.videoUrl}
                    autoPlay={banner.autoplay}
                    playsInline
                    muted={banner.muted}
                    loop={banner.loop}
                    disablePictureInPicture
                  />
                  <div 
                    className="mainbanner-video-play-button" 
                    onClick={(e) => {
                      e.stopPropagation();
                      const video = document.querySelector(`.mainbanner-list-item:nth-child(${index + 1}) video`) as HTMLVideoElement;
                      if (video) {
                        if (video.paused) {
                          video.play().catch(error => console.error(t('비디오 재생 중 오류 발생:'), error));
                        } else {
                          video.pause();
                        }
                      }
                    }}
                    style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      backgroundColor: 'rgba(0, 0, 0, 0.5)',
                      color: 'white',
                      borderRadius: '50%',
                      width: '60px',
                      height: '60px',
                      display: 'none',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      opacity: '0.8',
                      transition: 'opacity 0.2s',
                      zIndex: 10
                    }}
                    onMouseOver={(e) => {
                      (e.currentTarget as HTMLDivElement).style.opacity = '1';
                    }}
                    onMouseOut={(e) => {
                      (e.currentTarget as HTMLDivElement).style.opacity = '0.8';
                    }}
                  >
                    {(() => {
                      // 비디오 요소 확인 및 상태 체크
                      const videoEl = document.querySelector(`.mainbanner-list-item:nth-child(${index + 1}) video`) as HTMLVideoElement;
                      const isPaused = !videoEl || videoEl.paused;
                      
                      return isPaused ? (
                        // 재생 아이콘
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M8 5V19L19 12L8 5Z" fill="white" />
                        </svg>
                      ) : (
                        // 정지 아이콘
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <rect x="6" y="4" width="4" height="16" fill="white" />
                          <rect x="14" y="4" width="4" height="16" fill="white" />
                        </svg>
                      );
                    })()}
                  </div>
                </div>
              ) : (
                isMobile ? (
                  <div 
                    className="mainbanner-slide-content" 
                    style={{ backgroundImage: `url(${imgSrc})` }}
                  />
                ) : (
                  <img 
                    className="mainbanner-image" 
                    src={imgSrc} 
                    alt={banner.text || t('배너 이미지')} 
                  />
                )
              )}
              
              {(banner.showTitle || banner.showButton) && (
                <div 
                  className={`mainbanner-content position-${banner.position} ${banner.mobileFullWidth ? 'mobile-full-width' : ''} ${banner.hasBackground ? 'has-background' : ''}`}
                  style={{ 
                    color: banner.textColor || 'white',
                    textShadow: banner.textShadow || '0 1px 2px rgba(0, 0, 0, 0.6)'
                  }}
                >
                  {banner.showTitle && banner.description && (
                    <p className="mainbanner-description">{t(banner.description)}</p>
                  )}
                  {banner.showTitle && banner.text && (
                    <h2 className="mainbanner-title">{t(banner.text)}</h2>
                  )}
                  
                  {banner.showButton && banner.url && banner.url !== '#' && (
                    <button 
                      className={`mainbanner-cta-button ${banner.transparentButton ? 'transparent' : ''}`}
                      onClick={() => window.location.href = banner.url || '#'}
                      style={{
                        backgroundColor: banner.transparentButton ? 'transparent' : (banner.buttonBgColor || '#ff6b6b'),
                        color: banner.buttonTextColor || 'white',
                        borderColor: banner.buttonTextColor || 'white',
                        width: isMobile ? (banner.mobileButtonWidth || banner.buttonWidth || 'auto') : (banner.buttonWidth || 'auto'),
                        height: isMobile ? (banner.mobileButtonHeight || banner.buttonHeight || 'auto') : (banner.buttonHeight || 'auto')
                      }}
                    >
                      {t(banner.buttonText || '자세히 보기')}
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  // 표준 슬라이더 모드
  return (
    <div className={`mainbanner-slider-container ${className}`} style={style}>
      <div className="mainbanner-slider-wrapper">
        {banners.map((banner, index) => {
          const customStyle = typeof banner.style === 'string' ? parseStyleString(banner.style) : (banner.style || {});
          const imgSrc = getImageSource(banner, index);
          const isVideo = banner.mediaType === 'video' && banner.videoUrl;
          
          return (
            <div key={index} className={`mainbanner-slide ${index === currentIndex ? 'active' : ''}`} style={customStyle}>
              {isVideo ? (
                <div 
                  className="mainbanner-video-container" 
                  onClick={index === currentIndex ? handleVideoClick : undefined} 
                  onMouseEnter={index === currentIndex ? handleMouseEnter : undefined}
                  onMouseLeave={index === currentIndex ? handleMouseLeave : undefined}
                  style={{ width: '100%', height: '100%', cursor: index === currentIndex ? 'pointer' : 'default', position: 'relative' }}
                >
                  <video
                    ref={index === currentIndex ? videoRef : null}
                    className="mainbanner-video"
                    src={banner.videoUrl}
                    autoPlay={index === currentIndex && banner.autoplay}
                    playsInline
                    muted={banner.muted}
                    loop={banner.loop}
                    preload="auto"
                    disablePictureInPicture
                    onError={handleVideoError}
                    onLoadedData={handleVideoLoaded}
                    onPlay={handleVideoPlaying}
                    onEnded={() => !banner.loop && index === currentIndex && handleVideoEnded()}
                  />
                  {index === currentIndex && (
                    <div 
                      className="mainbanner-video-play-button" 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleVideoClick();
                      }}
                      style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        color: 'white',
                        borderRadius: '50%',
                        width: '60px',
                        height: '60px',
                        display: isHovering || !isVideoPlaying ? 'flex' : 'none',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        opacity: '0.8',
                        transition: 'opacity 0.2s',
                        zIndex: 10
                      }}
                      onMouseOver={(e) => {
                        (e.currentTarget as HTMLDivElement).style.opacity = '1';
                      }}
                      onMouseOut={(e) => {
                        (e.currentTarget as HTMLDivElement).style.opacity = '0.8';
                      }}
                    >
                      {isVideoPlaying ? (
                        // 정지 아이콘
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <rect x="6" y="4" width="4" height="16" fill="white" />
                          <rect x="14" y="4" width="4" height="16" fill="white" />
                        </svg>
                      ) : (
                        // 재생 아이콘
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M8 5V19L19 12L8 5Z" fill="white" />
                        </svg>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                isMobile ? (
                  <div 
                    className="mainbanner-slide-content" 
                    style={{ backgroundImage: `url(${imgSrc})` }}
                  />
                ) : (
                  <img 
                    className="mainbanner-image" 
                    src={imgSrc} 
                    alt={banner.text || t('배너 이미지')} 
                  />
                )
              )}
              
              {(banner.showTitle || banner.showButton) && (
                <div 
                  className={`mainbanner-content position-${banner.position} ${banner.mobileFullWidth ? 'mobile-full-width' : ''} ${banner.hasBackground ? 'has-background' : ''}`}
                  style={{ 
                    color: banner.textColor || 'white',
                    textShadow: banner.textShadow || '0 1px 2px rgba(0, 0, 0, 0.6)'
                  }}
                >
                  {banner.showTitle && banner.description && (
                    <p className="mainbanner-description">{t(banner.description)}</p>
                  )}
                  {banner.showTitle && banner.text && (
                    <h2 className="mainbanner-title">{t(banner.text)}</h2>
                  )}
                  
                  {banner.showButton && banner.url && banner.url !== '#' && (
                    <button 
                      className={`mainbanner-cta-button ${banner.transparentButton ? 'transparent' : ''}`}
                      onClick={() => window.location.href = banner.url || '#'}
                      style={{
                        backgroundColor: banner.transparentButton ? 'transparent' : (banner.buttonBgColor || '#ff6b6b'),
                        color: banner.buttonTextColor || 'white',
                        borderColor: banner.buttonTextColor || 'white',
                        width: isMobile ? (banner.mobileButtonWidth || banner.buttonWidth || 'auto') : (banner.buttonWidth || 'auto'),
                        height: isMobile ? (banner.mobileButtonHeight || banner.buttonHeight || 'auto') : (banner.buttonHeight || 'auto')
                      }}
                    >
                      {t(banner.buttonText || '자세히 보기')}
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
      
      {/* 네비게이션 버튼 */}
      {banners.length > 1 && (
        <>
          <button className="mainbanner-nav-button prev" onClick={prevBanner}>&#10094;</button>
          <button className="mainbanner-nav-button next" onClick={nextBanner}>&#10095;</button>
          
          {/* 인디케이터 */}
          <div className="mainbanner-indicators">
            {banners.map((_, index) => (
              <div 
                key={index} 
                className={`mainbanner-indicator ${index === currentIndex ? 'active' : ''}`}
                onClick={() => goToBanner(index)}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default MainBannerComponent; 